import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, Button, Result } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

interface Props {
    children: ReactNode;
    fallbackComponent?: React.ComponentType<{ error: Error; resetError: () => void }>;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
        };
    }

    static getDerivedStateFromError(error: Error): State {
        return {
            hasError: true,
            error,
        };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }
    }

    resetError = (): void => {
        this.setState({
            hasError: false,
            error: null,
        });
    };

    render(): ReactNode {
        const { hasError, error } = this.state;
        const { children, fallbackComponent: FallbackComponent } = this.props;

        if (hasError) {
            if (FallbackComponent) {
                return <FallbackComponent error={error!} resetError={this.resetError} />;
            }

            return (
                <Result
                    status="warning"
                    title="Problème de chargement"
                    subTitle="Nous n'avons pas pu charger le contenu après plusieurs tentatives."
                    extra={
                        <Button
                            type="primary"
                            icon={<ReloadOutlined />}
                            onClick={this.resetError}
                        >
                            Réessayer
                        </Button>
                    }
                />
            );
        }

        return children;
    }
}

// Default fallback component for ad fetching errors
export const AdFetchingErrorFallback: React.FC<{
    error?: Error;
    resetError?: () => void;
    retryFetch?: () => void;
    isLoading?: boolean;
}> = ({ resetError, retryFetch, isLoading = false }) => {
    const handleRetry = () => {
        if (retryFetch) {
            retryFetch();
        }
        if (resetError) {
            resetError();
        }
    };

    return (
        <Alert
            type="warning"
            message="Impossible de charger les annonces"
            description="Nous n'avons pas pu charger les annonces après plusieurs tentatives. Veuillez réessayer plus tard."
            showIcon
            action={
                <Button
                    size="small"
                    type="primary"
                    onClick={handleRetry}
                    loading={isLoading}
                    icon={<ReloadOutlined />}
                >
                    Réessayer
                </Button>
            }
        />
    );
};

export default ErrorBoundary; 